package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.core.util.parallelMap
import br.com.vvrefeicoes.portal.domain.model.DEFAULT_ADMIN_OTHERS_PERCENT
import br.com.vvrefeicoes.portal.domain.model.EntryRow
import br.com.vvrefeicoes.portal.domain.model.EntryRowLabel
import br.com.vvrefeicoes.portal.domain.model.EntryTable
import br.com.vvrefeicoes.portal.domain.model.EntryTableKind
import br.com.vvrefeicoes.portal.domain.model.Installment
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.OperationalEntry
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryCatalog
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryEvent
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryEventType
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryKind
import br.com.vvrefeicoes.portal.domain.model.OperationalEntryStatus
import br.com.vvrefeicoes.portal.domain.model.PendingEntryUnit
import br.com.vvrefeicoes.portal.domain.model.StaffMovement
import br.com.vvrefeicoes.portal.domain.model.StaffMovementType
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.OperationalEntryRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject

class RemoteOperationalEntryRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : OperationalEntryRepository {
    override suspend fun load(
        unidadeIds: List<String>,
        period: MonthPeriod,
        kind: OperationalEntryKind,
    ): List<OperationalEntry> =
        unidadeIds
            .parallelMap { unidadeId ->
                get<List<OperationalEntryDto>>(
                    "/operational/entries?unitId=$unidadeId&year=${period.year}&month=${period.month}&type=${kind.name}",
                ).maxByOrNull { it.versao }
                    ?.toDomain()
            }.filterNotNull()

    override suspend fun pendingApprovals(): List<PendingEntryUnit> = get<PendingEntriesDto>("/operational/entries/pending-approvals").units.map { it.toDomain() }

    override suspend fun pendingDrafts(): List<PendingEntryUnit> = get<PendingEntriesDto>("/operational/entries/pending-drafts").units.map { it.toDomain() }

    override suspend fun save(entry: OperationalEntry): OperationalEntry =
        if (isUuid(entry.id)) {
            put<OperationalEntryDto>("/operational/entries/${entry.id}", entry.toRequest()).toDomain()
        } else {
            post<OperationalEntryDto>("/operational/entries", entry.toRequest()).toDomain()
        }

    override suspend fun submit(entry: OperationalEntry): OperationalEntry = postEmpty<OperationalEntryDto>("/operational/entries/${ensureCreated(entry)}/submit").toDomain()

    override suspend fun approve(entry: OperationalEntry): OperationalEntry = postEmpty<OperationalEntryDto>("/operational/entries/${ensureCreated(entry)}/approve").toDomain()

    override suspend fun reject(
        entry: OperationalEntry,
        reason: String,
    ): OperationalEntry = post<OperationalEntryDto>("/operational/entries/${ensureCreated(entry)}/reject", RejectDto(reason)).toDomain()

    private suspend fun ensureCreated(entry: OperationalEntry): String =
        if (isUuid(entry.id)) {
            entry.id
        } else {
            post<OperationalEntryDto>("/operational/entries", entry.toRequest()).id
        }

    private suspend inline fun <reified T> get(path: String): T = request { httpClient.get(baseUrl + path) { authorize() } }.body()

    private suspend inline fun <reified T> post(
        path: String,
        body: Any,
    ): T = request { httpClient.post(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun <reified T> postEmpty(path: String): T = request { httpClient.post(baseUrl + path) { authorize() } }.body()

    private suspend inline fun <reified T> put(
        path: String,
        body: Any,
    ): T = request { httpClient.put(baseUrl + path) { jsonBody(body) } }.body()

    private suspend inline fun request(
        crossinline call: suspend () -> HttpResponse,
    ): HttpResponse {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<OperationalApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }

    private fun isUuid(value: String): Boolean = UUID_PATTERN.matches(value)

    private companion object {
        val UUID_PATTERN = Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")
    }
}

@Serializable
private data class RejectDto(
    val reason: String,
)

@Serializable
private data class OperationalApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class OperationalEntryRequestDto(
    val organogramaUnidadeId: String,
    val ano: Int,
    val mes: Int,
    val tipo: String,
    val adminOthersPercent: Double,
    val corporatePayrollCents: Long,
    val headcount: Int,
    val rows: List<OperationalRowRequestDto>,
    val staffMovements: List<OperationalStaffRequestDto>,
    val satisfaction: List<JsonObject> = emptyList(),
    val complaints: List<JsonObject> = emptyList(),
)

@Serializable
private data class OperationalRowRequestDto(
    val categoria: String,
    val kind: String,
    val rowKey: String,
    val supplierId: String? = null,
    val supplierName: String? = null,
    val installmentTotal: Int? = null,
    val installmentCurrent: Int? = null,
    val weeks: List<Long?> = emptyList(),
)

@Serializable
private data class OperationalStaffRequestDto(
    val movement: String,
    val role: String,
    val quantity: Int,
)

@Serializable
private data class PendingEntriesDto(
    val units: List<PendingEntryDto> = emptyList(),
)

@Serializable
private data class PendingEntryDto(
    val id: String,
    val name: String,
    val year: Int,
    val month: Int,
) {
    fun toDomain(): PendingEntryUnit = PendingEntryUnit(id = id, name = name, year = year, month = month)
}

@Serializable
private data class OperationalEntryDto(
    val id: String,
    val organogramaUnidadeId: String = "",
    val ano: Int,
    val mes: Int,
    val tipo: String,
    val versao: Int = 1,
    val status: String,
    val oficial: Boolean = false,
    val corporatePayrollCents: Long = 0,
    val headcount: Int = 0,
    val adminOthersPercent: Double? = null,
    val editable: Boolean = false,
    val submittedAt: String? = null,
    val submittedBy: String? = null,
    val reviewedAt: String? = null,
    val reviewedBy: String? = null,
    val rejectionReason: String? = null,
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val createdBy: String? = null,
    val updatedBy: String? = null,
    val rows: List<OperationalRowDto> = emptyList(),
    val staffMovements: List<OperationalStaffDto> = emptyList(),
    val events: List<OperationalEventDto> = emptyList(),
    val satisfaction: List<JsonObject> = emptyList(),
    val complaints: List<JsonObject> = emptyList(),
)

@Serializable
private data class OperationalRowDto(
    val id: String,
    val categoria: String,
    val kind: String,
    val rowKey: String? = null,
    val supplierId: String? = null,
    val supplierName: String? = null,
    val installmentTotal: Int? = null,
    val installmentCurrent: Int? = null,
    val position: Int = 0,
    val weeks: List<Long?> = emptyList(),
)

@Serializable
private data class OperationalStaffDto(
    val movement: String,
    val role: String = "",
    val quantity: Int = 0,
)

@Serializable
private data class OperationalEventDto(
    val type: String,
    val actorName: String? = null,
    val at: String? = null,
    val note: String? = null,
)

private fun OperationalEntry.toRequest(): OperationalEntryRequestDto =
    OperationalEntryRequestDto(
        organogramaUnidadeId = unidadeId,
        ano = period.year,
        mes = period.month,
        tipo = kind.name,
        adminOthersPercent = adminOthersPercent,
        corporatePayrollCents = corporatePayrollCents,
        headcount = 0,
        rows =
            tables.flatMap { table ->
                table.rows.map { row ->
                    OperationalRowRequestDto(
                        categoria = table.id,
                        kind = if (table.kind == EntryTableKind.REVENUE) "RECEITA" else "DESPESA",
                        rowKey = row.id,
                        supplierId = row.supplierId,
                        supplierName = (row.label as? EntryRowLabel.Supplier)?.value,
                        installmentTotal = row.installment?.total,
                        installmentCurrent = row.installment?.current,
                        weeks = row.weeks,
                    )
                }
            },
        staffMovements = staffMovements.map { OperationalStaffRequestDto(it.movement.name, it.role, it.quantity) },
    )

private fun OperationalEntryDto.toDomain(): OperationalEntry {
    val submittedEvent = events.lastOrNull { it.type.equals("SUBMITTED", ignoreCase = true) }
    val reviewedEvent = events.lastOrNull { it.type.equals("APPROVED", ignoreCase = true) || it.type.equals("REJECTED", ignoreCase = true) }
    return OperationalEntry(
        id = id,
        unidadeId = organogramaUnidadeId,
        kind = tipo.toKind(),
        companyId = 0,
        companyName = "",
        period = MonthPeriod(ano, mes),
        tables =
            OperationalEntryCatalog.tables.map { table ->
                table.copy(rows = rows.filter { it.categoria == table.id }.sortedBy { it.position }.map { it.toDomainRow(table) })
            },
        status = status.toStatus(),
        versao = versao,
        oficial = oficial,
        serverEditable = editable,
        submittedBy = submittedEvent?.actorName.orEmpty(),
        submittedAt = submittedEvent?.at?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        reviewedBy = reviewedEvent?.actorName.orEmpty(),
        reviewedAt = reviewedEvent?.at?.takeIf { it.isNotBlank() }?.let(::formatIsoDateTime),
        rejectionReason = rejectionReason.orEmpty(),
        updatedBy = events.lastOrNull()?.actorName.orEmpty(),
        updatedAt = formatIsoDateTime(updatedAt),
        corporatePayrollCents = corporatePayrollCents,
        adminOthersPercent = adminOthersPercent ?: DEFAULT_ADMIN_OTHERS_PERCENT,
        staffMovements = staffMovements.mapNotNull { it.toDomain() },
        history = events.map { it.toDomain() },
    )
}

private fun OperationalRowDto.toDomainRow(table: EntryTable): EntryRow {
    val rowId = rowKey?.takeIf { it.isNotBlank() } ?: id
    val label =
        when (table.kind) {
            EntryTableKind.REVENUE -> table.rows.firstOrNull { it.id == rowId }?.label ?: EntryRowLabel.Resource(table.title)
            EntryTableKind.EXPENSE -> EntryRowLabel.Supplier(supplierName.orEmpty())
        }
    return EntryRow(
        id = rowId,
        label = label,
        weeks = weeks,
        supplierId = supplierId,
        installment = installmentTotal?.let { Installment(total = it, current = installmentCurrent ?: 0) },
    )
}

private fun OperationalStaffDto.toDomain(): StaffMovement? = movement.toMovementType()?.let { StaffMovement(movement = it, role = role, quantity = quantity) }

private fun OperationalEventDto.toDomain(): OperationalEntryEvent =
    OperationalEntryEvent(
        type = type.toEventType(),
        actorName = actorName.orEmpty(),
        at = formatIsoDateTime(at),
        note = note.orEmpty(),
    )

private fun String.toStatus(): OperationalEntryStatus =
    when (uppercase()) {
        "ENVIADO" -> OperationalEntryStatus.SUBMITTED
        "APROVADO" -> OperationalEntryStatus.APPROVED
        "REJEITADO" -> OperationalEntryStatus.REJECTED
        else -> OperationalEntryStatus.DRAFT
    }

private fun String.toKind(): OperationalEntryKind =
    when (uppercase()) {
        OperationalEntryKind.PROVISIONADOS_MES.name -> OperationalEntryKind.PROVISIONADOS_MES
        else -> OperationalEntryKind.GESTAO_FINANCEIRA
    }

private fun String.toEventType(): OperationalEntryEventType =
    when (uppercase()) {
        "SUBMITTED" -> OperationalEntryEventType.SUBMITTED
        "APPROVED" -> OperationalEntryEventType.APPROVED
        "REJECTED" -> OperationalEntryEventType.REJECTED
        else -> OperationalEntryEventType.SAVED
    }

private fun String.toMovementType(): StaffMovementType? =
    when (uppercase()) {
        "HIRED" -> StaffMovementType.HIRED
        "TERMINATED" -> StaffMovementType.TERMINATED
        "TRANSFERRED" -> StaffMovementType.TRANSFERRED
        "ACQUIRED" -> StaffMovementType.ACQUIRED
        else -> null
    }
